"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SignInSchema } from "../../auth.schema";
import { useSignInMutation } from "../../auth.mutations";

interface SignInFormProps {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function SignInForm({ onSuccess, onError }: SignInFormProps) {
  const signInMutation = useSignInMutation();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof SignInSchema>>({
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof SignInSchema>) {
    try {
      const response = await signInMutation.mutateAsync(values);

      if (response.status === "success") {
        onSuccess?.();
      } else {
        onError?.(response);
      }
    } catch (error: any) {
      onError?.(error);
    }
  }

  return (
    <Card className="rounded-2xl bg-gray-30 dark:bg-black/10 ">
      <CardHeader>
        <CardTitle className="text-2xl">Entrar</CardTitle>
        <CardDescription>Insira seu e-mail e senha para acessar sua conta</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="seu.email@exemplo.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center">
                    <FormLabel>Senha</FormLabel>
                    <Link
                      href="/reset-password"
                      className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                    >
                      Esqueceu sua senha?
                    </Link>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder="Digite sua senha"
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        )}
                        <span className="sr-only">{showPassword ? "Ocultar senha" : "Mostrar senha"}</span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full cursor-pointer" disabled={signInMutation.isPending}>
              {signInMutation.isPending ? "Entrando..." : "Entrar"}
            </Button>

            <div className="flex flex-col gap-2 hidden">
              <Button variant="outline" className="w-full">
                <FcGoogle className="mr-2 h-5 w-5" />
                Entrar com Google
              </Button>
            </div>
          </form>
        </Form>

        <div className="mt-2 text-left text-sm">
          Não tem uma conta?{" "}
          <Link href="/sign-up" className="underline underline-offset-4">
            Cadastre-se
          </Link>
        </div>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          <p>Ao continuar, você concorda com nossos</p>
          <Link href="/privacy" className="underline underline-offset-4 hover:text-primary">
            Política de Privacidade
          </Link>{" "}
          e{" "}
          <Link href="/terms" className="underline underline-offset-4 hover:text-primary">
            Termos de Uso
          </Link>
          .
        </div>
      </CardContent>
    </Card>
  );
}
